import axios from 'axios';
import * as Sentry from '@sentry/react';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { getEmailAccessToken, getAuthToken, getRefreshToken, setNewTokenID } from '../TokenHandler';

const axiosMainInstance = axios.create();
const axiosRefreshInstance = axios.create();

const clearSession = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('idToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('userEmail');
};

const redirectToLogin = () => {
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
};

const refreshAuth = async (failedRequest) => {
  if (failedRequest?.config?.url?.includes('/auth/validate-jwt')) {
    return Promise.resolve();
  }

  const refreshToken = getRefreshToken();
  const email = localStorage.getItem('userEmail');

  if (!refreshToken || !email) {
    clearSession();
    redirectToLogin();
    return Promise.reject(new Error('No refresh token available'));
  }

  try {
    const tokenRefreshResponse = await axiosRefreshInstance.post(
      `${process.env.REACT_APP_CIELO_API_URL}auth/refresh-token`,
      { email, refreshToken }
    );

    const data = tokenRefreshResponse?.data;
    const newIdToken = data?.id_token || data?.access_token;
    const newRefreshToken = data?.refresh_token;

    if (!newIdToken) {
      clearSession();
      redirectToLogin();
      return Promise.reject(new Error('Error with the response'));
    }

    setNewTokenID(newIdToken);
    if (data?.access_token) {
      localStorage.setItem('accessToken', data.access_token);
    }
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    }

    failedRequest.config.headers['Authorization'] = `Bearer ${newIdToken}`;
    return Promise.resolve();
  } catch (error) {
    clearSession();
    redirectToLogin();
    return Promise.reject(error);
  }
};

createAuthRefreshInterceptor(axiosMainInstance, refreshAuth, {
  pauseInstanceWhileRefreshing: true
});

axiosMainInstance.interceptors.request.use(
  (config) => {
    const token = getAuthToken() || getEmailAccessToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosMainInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const { config, response } = error || {};

    Sentry.withScope((scope) => {
      scope.setTag('type', 'network_error');
      scope.setTag('endpoint', config?.url || 'unknown');
      scope.setTag('method', config?.method?.toUpperCase() || 'unknown');
      scope.setTag('status', response?.status || 'no_response');

      scope.setContext('request', {
        endpoint: `${config?.baseURL || ''}${config?.url || ''}`,
        method: config?.method?.toUpperCase()
      });

      scope.setContext('response', {
        status: String(response?.status || ''),
        statusText: response?.statusText || '',
        data: response?.data ? JSON.stringify(response.data) : null
      });

      Sentry.captureException(
        new Error(
          `Network error ${response?.status || 'NO_RESPONSE'}: ${config?.method?.toUpperCase()} ${
            config?.url
          }`
        )
      );
    });

    return Promise.reject(error);
  }
);

const httpClient = (config = {}) => {
  const axiosConfig = {
    ...config,
    baseURL: config.baseURL || process.env.REACT_APP_CIELO_API_URL,
    // baseURL: config.baseURL || 'http://localhost:3333/',
    headers: {
      ...config.headers
    }
  };

  return axiosMainInstance(axiosConfig)
    .then((resp) => {
      const { data, pagination, info } = resp.data;

      if (!pagination) {
        return resp;
      } else {
        return { data, pagination, info };
      }
    })
    .catch(async (error) => {
      console.log('error', error);
      const { response, message } = error || {};

      if (response) {
        const { status, data } = response;

        if (data.error) {
          throw data;
        }

        if (status === 400 || status === 403) {
          throw data;
        }

        if (status) {
          throw data.detail || data.message || message;
        }
      }

      throw message;
    });
};

export default httpClient;
